import { createFileRoute, Link } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { PublicOrAppShell } from "@/components/public-or-app-shell";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — Cailève" },
      {
        name: "description",
        content:
          "The terms governing your use of Cailève, your personal styling concierge.",
      },
      { property: "og:title", content: "Terms of Service — Cailève" },
      {
        property: "og:description",
        content:
          "The terms governing your use of Cailève, your personal styling concierge.",
      },
    ],
  }),
  component: () => (
    <PublicOrAppShell>
      <TermsPage />
    </PublicOrAppShell>
  ),
});

function TermsPage() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--caileve-bg)", color: "var(--caileve-ink)" }}>
      <div className="max-w-3xl mx-auto px-8 py-16">
        <Link
          to={user ? "/account" : "/login"}
          className="text-[11px] uppercase tracking-[0.25em] text-[var(--caileve-lilac-text)] hover:text-[var(--caileve-ink)]"
        >
          ← {user ? "Back to account" : "Back to sign in"}
        </Link>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--caileve-lilac-text)]">Legal</p>
      <h1
        className="font-serif-display text-5xl md:text-6xl mt-3 leading-[1.05]"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        Terms of Service
      </h1>
      <p className="text-xs uppercase tracking-[0.25em] text-[var(--caileve-copper)] mt-6">
        Effective — May 29, 2026
      </p>

      <div className="flex justify-center my-12">
        <div className="w-16 h-px bg-[var(--caileve-copper)]" />
      </div>

      <Section title="Acceptance of terms">
        By creating an account or using Cailève, a service provided by Écrin Style LLC,
        you agree to these Terms of Service and to our Privacy Policy. If you do not
        agree, please do not use the service.
      </Section>

      <Section title="The service">
        Cailève, operated by Écrin Style LLC, is a personal styling assistant that allows you to
        catalog your jewelry and clothing, compose looks, save styling
        suggestions, and record how those looks make you feel. The service is
        provided for your personal, non-commercial use.
      </Section>

      <Section title="Your responsibilities">
        <ul className="space-y-3 list-disc pl-5">
          <li>
            You are responsible for safeguarding the password and credentials of
            your account.
          </li>
          <li>
            You agree to upload only content that you own or have the right to
            upload, and to refrain from uploading anything unlawful, infringing,
            or harmful to others.
          </li>
          <li>
            You agree not to attempt to access another user's account, to
            disrupt the service, or to misuse the underlying infrastructure.
          </li>
        </ul>
      </Section>

      <Section title="Intellectual property">
        <p>
          You retain full ownership of all content you upload — your photos,
          item details, looks, notes, and feelings. By using the service, you
          grant Écrin Style LLC only the limited technical license needed to
          store and display your content back to you within the app.
        </p>
        <p>
          The Cailève name, design, and software are the property of
          Écrin Style LLC and may not be copied, redistributed, or used to
          create a competing product without written permission.
        </p>
      </Section>

      <Section title="Termination">
        You may delete your account at any time from the My Account screen,
        which will permanently remove your data as described in the Privacy
        Policy. We may suspend or terminate accounts that violate these terms.
      </Section>

      <Section title="Limitation of liability">
        The service is provided on an as-is and as-available basis, without
        warranties of any kind, express or implied. To the fullest extent
        permitted by law, Écrin Style LLC and its affiliates will not be
        liable for any indirect, incidental, special, consequential, or
        punitive damages, or any loss of profits or revenues, arising from your
        use of the service.
      </Section>

      <Section title="Governing law">
        These terms are governed by and construed in accordance with the laws
        of the State of New Jersey, United States, without regard to its
        conflict of laws principles. Any disputes arising out of or relating to
        the service shall be subject to the exclusive jurisdiction of the
        state and federal courts located in New Jersey.
      </Section>

      <Section title="Contact">
        General questions may be directed to{" "}
        <a
          href="mailto:hello@caileve.app"
          className="text-[var(--caileve-lilac)] hover:text-[var(--caileve-copper)] transition-colors"
        >
          hello@caileve.app
        </a>
        . Legal and administrative notices should be sent to{" "}
        <a
          href="mailto:admin@caileve.app"
          className="text-[var(--caileve-lilac)] hover:text-[var(--caileve-copper)] transition-colors"
        >
          admin@caileve.app
        </a>
        .
      </Section>

      <div className="flex justify-center my-12">
        <div className="w-16 h-px bg-[var(--caileve-copper)]" />
      </div>

      <div className="text-center space-y-3">
        <Link
          to="/privacy"
          className="text-[var(--caileve-lilac)] hover:text-[var(--caileve-copper)] transition-colors text-sm"
        >
          Read our Privacy Policy
        </Link>
        <p className="text-xs text-[var(--caileve-lilac)]">
          Copyright 2026 Écrin Style LLC. All rights reserved.
        </p>
      </div>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-10">
      <h2
        className="font-serif-display text-2xl md:text-3xl mb-4 text-[var(--caileve-ink)]"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        {title}
      </h2>
      <div
        className="space-y-4 leading-relaxed text-[var(--caileve-ink)]/85"
        style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: "1.15rem",
        }}
      >
        {children}
      </div>
    </section>
  );
}